import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Check, Plus, Share2, Star, Users } from "lucide-react";
import { Link as RouterLink, useSearchParams } from "react-router-dom";
import { Calendar, dateFnsLocalizer } from "react-big-calendar";
import withDragAndDrop from "react-big-calendar/lib/addons/dragAndDrop";
import {
  format,
  parse,
  startOfWeek,
  getDay,
  isBefore,
  startOfDay,
  startOfHour,
} from "date-fns";
import { enUS } from "date-fns/locale";
import { useAuth } from "../../app/useAuth";
import { useLinkedCalendars } from "../../hooks/useLinkedCalendars";
import { useAvailabilityEvents } from "../../hooks/useAvailabilityEvents";
import { useAvailabilityMarks } from "../../hooks/useAvailabilityMarks";
import { useEventHideOverrides } from "../../hooks/useEventHideOverrides";
import { useFinalizedSessions } from "../../hooks/useFinalizedSessions";
import { useQuestingGroups } from "../../hooks/useQuestingGroups";
import { useUserSettings } from "../../hooks/useUserSettings";
import { useAutoVoteSettings } from "../../hooks/useAutoVoteSettings";
import { useIsMobile } from "../../hooks/useIsMobile";
import { SessionCalendarControl } from "../../components/session-calendar-control";
import { setMarkPreferences } from "../../lib/data/settings";
import {
  setAutoVoteMode,
  migrateLegacyAutoVote,
  setAutoRsvpMode,
  setAutoRsvpConflictTarget,
  isValidAutoRsvpMode,
  isValidAutoRsvpTarget,
} from "../../lib/data/availability";
import { useDebouncedMarkPersist } from "./state/use-debounced-mark-persist.js";
import { AvailabilitySettingsPopover } from "./components/AvailabilitySettingsPopover";
import { EmptyAvailabilityState } from "./components/EmptyAvailabilityState";
import { ErrorState } from "../../components/ui/error-state";
import { CalendarSurfaceSkeleton } from "../../components/ui/skeleton";
import { resolveLoadState } from "../../lib/load-state";
import { AvailabilityShareListModal } from "../friends/components/AvailabilityShareListModal";
import LinkCalendarDialog from "./components/LinkCalendarDialog";
import HideEventDialog from "./components/HideEventDialog";
import EditLinkedCalendarDialog from "./components/EditLinkedCalendarDialog";
import { MarkEditorDialog } from "./components/MarkEditorDialog";
import { MobileAvailabilityAgenda } from "./components/MobileAvailabilityAgenda";
import { selectVisibleEvents } from "./utils/visible-events";
import {
  pickStartHHMMFromNow,
  pickEndHHMMFromStart,
  composeUtcIso,
  addDaysLocal,
} from "./utils/mark-time";
import { AvailabilityBackfillDialog } from "./components/AvailabilityBackfillDialog";
import { makeMonthDateHeader } from "./components/MonthDateHeader";
import {
  buildAvailabilityCalendarEvents,
  buildEventStyleGetter,
} from "./utils/calendar-events";
import { useEnrichedCalendarEvents } from "../calendar-enrichment/useEnrichedCalendarEvents";
import {
  expandMarksToCalendarEvents,
  buildMarkStyleGetter,
} from "./utils/marks-render";
import { detectConflictDays } from "./utils/overlap";
import { bucketEventsByLocalDay, localDayBounds } from "../../lib/days";
import { isSingleDayMark } from "./utils/mark-bounds";
import { getBrowserTimeZone } from "../../lib/time";
import "react-big-calendar/lib/css/react-big-calendar.css";
import "react-big-calendar/lib/addons/dragAndDrop/styles.css";

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales: { "en-US": enUS },
});

const DragAndDropCalendar = withDragAndDrop(Calendar);

const FIFTEEN_MIN_MS = 15 * 60 * 1000;

/**
 * Phase 3b T0.5 — custom RBC event body for user-authored availability marks.
 * Renders a small corner icon (Star for PREFERRED, Check for FEASIBLE) plus
 * the mark title. The icon is what distinguishes user marks from synced-source
 * events at a glance. This component is wired into calendarComponents.event
 * with a kind === "mark" guard so it only fires for marks.
 */
function MarkEventBlock({ event }) {
  const r = event?.resource;
  if (r?.kind !== "mark") return <span>{event.title}</span>;
  const Icon = r.level === "PREFERRED" ? Star : Check;
  return (
    <>
      <Icon className="absolute left-1.5 top-0.5 h-3 w-3 opacity-70" aria-hidden />
      <span className="ml-4 truncate">
        {event.title}
      </span>
    </>
  );
}

export default function AvailabilityPage() {
  const { user } = useAuth();
  const { data: linkedCalendars, loading, error: linkedError } =
    useLinkedCalendars(user?.uid);
  // Shared loading→error→ready precedence (empty is handled inside CalendarLayout).
  const calendarsLoadState = resolveLoadState({ loading, error: linkedError });
  // Page-level read of cached events so the settings popover can show the
  // count next to the cache-controls section. CalendarLayout below makes its
  // own call; Firestore listeners de-dupe internally.
  const { data: cachedEvents } = useAvailabilityEvents(user?.uid);
  const [dialogOpen, setDialogOpen] = useState(false);

  const alreadyLinkedIds = linkedCalendars.map((c) => c.id);

  // Page-level settings (hoisted out of CalendarLayout in chunk A11 so the
  // gear-icon popover trigger — which lives in the page header alongside
  // "+ Link calendar" — can drive them).
  const {
    defaultMarkDurationHours,
    defaultMarkStartHour,
  } = useUserSettings();

  // New authoritative auto-vote settings (chunk C). The doc may not yet exist
  // for existing users — trigger lazy migration when loading=false && !docExists.
  const autoVoteSettings = useAutoVoteSettings(user?.uid);
  const migrationFiredRef = useRef(false);
  useEffect(() => {
    if (!user?.uid) return;
    if (autoVoteSettings.loading) return;
    if (autoVoteSettings.docExists) return;
    if (migrationFiredRef.current) return;
    migrationFiredRef.current = true;
    migrateLegacyAutoVote().catch((err) => {
      console.error("auto-vote migration failed", err);
      // Keep one-shot semantics — a page refresh will retry.
    });
  }, [user?.uid, autoVoteSettings.loading, autoVoteSettings.docExists]);

  const [autoVoteSaving, setAutoVoteSaving] = useState(false);
  const [autoRsvpSaving, setAutoRsvpSaving] = useState(false);
  const [prefSaving, setPrefSaving] = useState(false);

  // Auto-RSVP settings live on the same autoVoteSettings/settings doc; derive
  // with off/maybe defaults (matches the functions-side readAutoRsvpSettings).
  const autoRsvpMode = isValidAutoRsvpMode(autoVoteSettings.settings?.autoRsvpMode)
    ? autoVoteSettings.settings.autoRsvpMode
    : "off";
  const autoRsvpConflictTarget = isValidAutoRsvpTarget(
    autoVoteSettings.settings?.autoRsvpConflictTarget
  )
    ? autoVoteSettings.settings.autoRsvpConflictTarget
    : "maybe";

  async function handleAutoRsvpModeChange(nextMode) {
    if (!user?.uid || nextMode === autoRsvpMode) return;
    setAutoRsvpSaving(true);
    try {
      await setAutoRsvpMode(user.uid, nextMode);
    } finally {
      setAutoRsvpSaving(false);
    }
  }

  async function handleAutoRsvpConflictTargetChange(nextTarget) {
    if (!user?.uid || nextTarget === autoRsvpConflictTarget) return;
    setAutoRsvpSaving(true);
    try {
      await setAutoRsvpConflictTarget(user.uid, nextTarget);
    } finally {
      setAutoRsvpSaving(false);
    }
  }
  const [shareListModalOpen, setShareListModalOpen] = useState(false);
  // Backfill preview dialog state. Phase 3: opened on the first transition
  // from "off" → any non-off tier when there's no prior backfill run on
  // record. The dialog's onConfirmed callback writes setAutoVoteMode(uid,
  // pendingMode) so users can review (or skip) the proposed pre-fill before
  // auto-vote becomes active.
  const [backfillDialogOpen, setBackfillDialogOpen] = useState(false);
  const [pendingAutoVoteMode, setPendingAutoVoteMode] = useState(null);

  async function handleAutoVoteModeChange(nextMode) {
    if (!user?.uid) return;
    if (nextMode === autoVoteSettings.autoVoteMode) return;
    // First-time-out-of-off path: open the backfill preview dialog FIRST.
    // Defer the actual write to the dialog's onConfirmed callback so users
    // can review the proposed pre-fill before auto-vote becomes active.
    const isFirstTimeOn =
      autoVoteSettings.autoVoteMode === "off" &&
      nextMode !== "off" &&
      !autoVoteSettings.settings?.onboarding?.lastBackfillRunAt;
    if (isFirstTimeOn) {
      setPendingAutoVoteMode(nextMode);
      setBackfillDialogOpen(true);
      return;
    }
    // Default path: write directly.
    setAutoVoteSaving(true);
    try {
      await setAutoVoteMode(user.uid, nextMode);
    } finally {
      setAutoVoteSaving(false);
    }
  }

  async function handleBackfillConfirmed() {
    if (pendingAutoVoteMode === null) return;
    if (!user?.uid) {
      setPendingAutoVoteMode(null);
      return;
    }
    setAutoVoteSaving(true);
    try {
      await setAutoVoteMode(user.uid, pendingAutoVoteMode);
    } finally {
      setAutoVoteSaving(false);
      setPendingAutoVoteMode(null);
    }
  }
  async function handleDefaultDurationChange(hours) {
    if (!user?.uid) return;
    setPrefSaving(true);
    try {
      await setMarkPreferences(user.uid, { defaultMarkDurationHours: hours });
    } finally {
      setPrefSaving(false);
    }
  }
  async function handleDefaultStartHourChange(value) {
    if (!user?.uid) return;
    setPrefSaving(true);
    try {
      await setMarkPreferences(user.uid, { defaultMarkStartHour: value });
    } finally {
      setPrefSaving(false);
    }
  }

  // Global "Show hidden events" toggle, persisted in localStorage so it
  // survives refresh without a Firestore round-trip. Default ON.
  const [showHidden, setShowHidden] = useState(() => {
    try {
      const v = localStorage.getItem("availability:showHidden");
      return v === null ? true : v === "true";
    } catch {
      return true;
    }
  });
  function toggleShowHidden(next) {
    setShowHidden(next);
    try {
      localStorage.setItem("availability:showHidden", String(next));
    } catch {
      /* ignore */
    }
  }

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-center justify-between gap-y-3">
        <div className="min-w-0">
          <h1 className="text-2xl font-display font-semibold tracking-tight">My Availability</h1>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
            Link your Google calendars and see all your conflicts and free time
            in one place.
          </p>
        </div>
        <div className="flex flex-wrap items-center justify-end gap-2">
          <button
            type="button"
            onClick={() => setShareListModalOpen(true)}
            data-testid="share-availability-button"
            className="inline-flex items-center gap-2 rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
          >
            <Share2 className="h-4 w-4" /> Share availability
          </button>
          <button
            type="button"
            onClick={() => setDialogOpen(true)}
            className="inline-flex items-center gap-2 rounded-xl bg-brand-primary px-3 py-2 text-sm font-medium text-white shadow-sm hover:bg-brand-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          >
            <Plus className="h-4 w-4" /> Link calendar
          </button>
          <AvailabilitySettingsPopover
            autoVoteMode={autoVoteSettings.autoVoteMode}
            onAutoVoteModeChange={handleAutoVoteModeChange}
            autoVoteSaving={autoVoteSaving}
            autoRsvpMode={autoRsvpMode}
            autoRsvpConflictTarget={autoRsvpConflictTarget}
            onAutoRsvpModeChange={handleAutoRsvpModeChange}
            onAutoRsvpConflictTargetChange={handleAutoRsvpConflictTargetChange}
            // Keep the card inert until the settings snapshot resolves — until
            // then `settings` is null and the derived mode is the "off" default,
            // so an early click could silently clobber a real stored value.
            autoRsvpSaving={autoRsvpSaving || autoVoteSettings.loading}
            showHiddenValue={showHidden}
            onShowHiddenChange={toggleShowHidden}
            defaultDurationHours={defaultMarkDurationHours}
            onDefaultDurationChange={handleDefaultDurationChange}
            defaultStartHour={defaultMarkStartHour}
            onDefaultStartHourChange={handleDefaultStartHourChange}
            prefSaving={prefSaving}
            cachedEventCount={(cachedEvents || []).length}
            linkedCalendarCount={(linkedCalendars || []).length}
          />
        </div>
      </header>

      <AvailabilityShareListModal
        open={shareListModalOpen}
        onOpenChange={setShareListModalOpen}
      />

      {calendarsLoadState === "loading" && (
        <CalendarSurfaceSkeleton
          label="Loading your linked calendars…"
          className="rounded-2xl border border-slate-200 bg-white/60 p-6 dark:border-slate-700 dark:bg-slate-900/60"
        />
      )}

      {/* Audit A-4: a failed calendars listener used to render as the empty
          state ("no calendars linked"); surface the failure with a retry. */}
      {calendarsLoadState === "error" && (
        <ErrorState
          title="Couldn't load your calendars"
          message="We hit a problem loading your linked calendars and availability. Check your connection and try again."
          onRetry={() => window.location.reload()}
        />
      )}

      {calendarsLoadState === "ready" && (
        <CalendarLayout
          uid={user?.uid}
          linkedCalendars={linkedCalendars}
          showHidden={showHidden}
          onLinkClick={() => setDialogOpen(true)}
        />
      )}

      <LinkCalendarDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        alreadyLinkedIds={alreadyLinkedIds}
      />
      <AvailabilityBackfillDialog
        open={backfillDialogOpen}
        onOpenChange={setBackfillDialogOpen}
        onConfirmed={handleBackfillConfirmed}
        displayTimeZone={Intl.DateTimeFormat().resolvedOptions().timeZone}
      />
    </div>
  );
}

function CalendarLayout({ uid, linkedCalendars, showHidden, onLinkClick }) {
  const { data: externalEvents } = useAvailabilityEvents(uid);
  const { data: finalizedSessions } = useFinalizedSessions(uid);
  const { data: hideOverrides } = useEventHideOverrides(uid);
  const { data: marks } = useAvailabilityMarks(uid);
  const { getGroupColor } = useQuestingGroups();
  // Re-read user settings here (cheap — Firestore subscription is shared/cached
  // by the hook) so onSelectSlot can apply the user's defaults to month-cell
  // clicks without threading more props through CalendarLayout.
  const {
    defaultMarkDurationHours,
    defaultMarkStartHour,
    publicIdentifierType,
    linkedCalendarEmail,
    shareEmailForCalendar,
  } = useUserSettings();
  // Viewer context for the unified add-to-calendar prominence model (§4.1).
  const calendarViewer = useMemo(
    () => ({ publicIdentifierType, linkedCalendarEmail, shareEmailForCalendar }),
    [publicIdentifierType, linkedCalendarEmail, shareEmailForCalendar]
  );
  const [hideTarget, setHideTarget] = useState(null);
  const [editTarget, setEditTarget] = useState(null);
  const [view, setView] = useState("month");
  const [date, setDate] = useState(new Date());
  // Below the md breakpoint, offer BOTH a tap-friendly day-grouped agenda (B2,
  // the default — best for managing marks) AND a side-scrollable calendar grid
  // (B2c) via a mobile toggle, so phones get the agenda/list + month/week/day
  // views (mirrors Google Calendar on mobile). The sidebar (with the "Add mark"
  // button + marks list) stays in both. Desktop is unchanged (grid only).
  const isMobile = useIsMobile();
  // Mobile-only sub-view: "agenda" (default) | "calendar". No effect on desktop.
  const [mobileCalView, setMobileCalView] = useState("agenda");
  // editorState shape: { mode: "create"|"edit", initial: {...} } | null
  const [editorState, setEditorState] = useState(null);
  // Optimistic placeholder for click-to-create flow: while the editor dialog
  // is open in create-mode, render a phantom mark on the calendar so the
  // user sees their click "drop" an entry behind the modal (Google Calendar
  // pattern). Cleared on dialog close — the persisted mark from Firestore
  // takes its place if Save was clicked, or it just vanishes if Cancelled.
  const [pendingNewMark, setPendingNewMark] = useState(null);

  // Optimistic overlay for drag/resize: when the user drops or resizes a
  // mark, RBC reports the new bounds, we apply them to local state IMMEDIATELY
  // (so the block snaps to its new position with zero perceptible delay), and
  // queue the actual Firestore write through the debouncer. When the
  // snapshot listener catches up with the persisted value, the convergence
  // effect below clears the now-redundant overlay entry. Map<markId, patch>.
  const [pendingPatches, setPendingPatches] = useState(() => new Map());

  // Drag/resize debouncer: collapses rapid same-mark drags into a single
  // Firestore write so transient positions don't trigger engine cascades.
  // Visual responsiveness is handled by the pendingPatches overlay above —
  // this hook only governs persistence cadence. `pagehide` and
  // `visibilitychange` flush pending writes immediately so navigation never
  // drops an edit (Firestore offline persistence covers the rare
  // unload-before-network-completes case).
  const { queueUpdate: queueMarkUpdate } = useDebouncedMarkPersist(uid);

  // Deep-link query params (D16): ?event=<id>, ?mark=<id>, ?slotConflict=<schedulerId:slotId>.
  // On mount (and whenever the relevant data resolves), focus the calendar to
  // the corresponding date and switch to week view. The slotConflict variant
  // is a v1 no-op (no extra Firestore fetch) — focus stays on today.
  const [searchParams] = useSearchParams();
  const highlightEventId = searchParams.get("event") || null;
  const highlightMarkId = searchParams.get("mark") || null;
  const highlightSlotConflict = searchParams.get("slotConflict") || null;
  const [highlightActive, setHighlightActive] = useState(false);
  const deepLinkAppliedRef = useRef(false);
  useEffect(() => {
    if (!highlightEventId && !highlightMarkId && !highlightSlotConflict) return;
    if (deepLinkAppliedRef.current) return;
    let focusDate = null;
    if (highlightEventId) {
      const ev = (externalEvents || []).find(
        (e) => e.eventId === highlightEventId || e.id === highlightEventId
      );
      if (ev?.startUtc) focusDate = new Date(ev.startUtc);
      else if (!externalEvents) return; // wait for data
    } else if (highlightMarkId) {
      const m = (marks || []).find((x) => x.id === highlightMarkId);
      if (m?.startUtc) focusDate = new Date(m.startUtc);
      else if (!marks) return; // wait for data
    } else if (highlightSlotConflict) {
      // v1: schedulerId:slotId — slot's date is not directly available without
      // an extra fetch. Keep focusDate=null (today). v2 can resolve via a
      // schedulers/slots subscription.
      focusDate = null;
    }
    deepLinkAppliedRef.current = true;
    if (focusDate) setDate(focusDate);
    setView("week");
    // On a phone the rendered surface is the agenda, which has no week view —
    // so a ?event=/?mark= deep link's highlighted week would never be seen.
    // Switch the mobile surface to the calendar so the highlight is visible
    // (audit A-6c). No-op on desktop (mobileCalView is mobile-only).
    setMobileCalView("calendar");
    setHighlightActive(true);
    // 8s gives the user time to find + read the highlighted event after the
    // page rerenders + the calendar scrolls. Was 3s, which faded before
    // most users could even spot the highlight.
    const t = setTimeout(() => setHighlightActive(false), 8000);
    return () => clearTimeout(t);
  }, [
    highlightEventId,
    highlightMarkId,
    highlightSlotConflict,
    externalEvents,
    marks,
  ]);

  // Reset the deep-link guard whenever the query params change so revisiting
  // the page with a different ?event=/?mark= re-fires the highlight effect.
  useEffect(() => {
    deepLinkAppliedRef.current = false;
  }, [highlightEventId, highlightMarkId, highlightSlotConflict]);

  const linkedCalendarsById = useMemo(
    () => Object.fromEntries(linkedCalendars.map((c) => [c.id, c])),
    [linkedCalendars]
  );

  const eventCountByCalendarId = useMemo(() => {
    const map = new Map();
    for (const e of externalEvents || []) {
      map.set(e.calendarId, (map.get(e.calendarId) || 0) + 1);
    }
    return map;
  }, [externalEvents]);

  // Enrich each finalized-session item with its parent scheduler's questing-
  // group color (when linked), so calendar events render in the group's color
  // and the upcoming-sessions panel can render the matching chip.
  const sessionsWithGroupColor = useMemo(
    () =>
      (finalizedSessions || []).map((s) => ({
        ...s,
        questingGroupColor: s.questingGroupId
          ? getGroupColor(s.questingGroupId)
          : null,
      })),
    [finalizedSessions, getGroupColor]
  );

  const allEventsStructural = useMemo(
    () =>
      buildAvailabilityCalendarEvents({
        externalEvents,
        finalizedSessions: sessionsWithGroupColor,
        linkedCalendarsById,
        hideOverrides,
      }),
    [externalEvents, sessionsWithGroupColor, linkedCalendarsById, hideOverrides]
  );

  // Resolve external-event titles via the privacy-preserving client
  // enrichment hook (titles are not persisted at rest — see
  // docs/calendar-cache-privacy-plan.md). Until a title resolves, RBC tiles
  // render time-only via an empty title; on resolve the array remounts with
  // the title in place.
  const allEvents = useEnrichedCalendarEvents(allEventsStructural);

  // Visible-window memo for mark expansion. Pads month views ±1 week so that
  // recurring marks straddling the cell grid render across the visible weeks.
  const visibleWindow = useMemo(() => {
    const d = date instanceof Date ? date : new Date(date);
    if (view === "month") {
      const first = new Date(d.getFullYear(), d.getMonth(), 1);
      const last = new Date(d.getFullYear(), d.getMonth() + 1, 1);
      return {
        start: new Date(first.getTime() - 7 * 24 * 3600 * 1000),
        end: new Date(last.getTime() + 7 * 24 * 3600 * 1000),
      };
    }
    if (view === "week") {
      const start = startOfWeek(d, { weekStartsOn: 0 });
      return { start, end: new Date(start.getTime() + 7 * 24 * 3600 * 1000) };
    }
    // day — DST-safe day end (next local midnight isn't always start + 24h).
    const start = startOfDay(d);
    return { start, end: localDayBounds(d).dayEnd };
  }, [date, view]);

  // Apply pendingPatches overlay to the live marks feed. effectiveMarks is
  // the source of truth for everything downstream (calendar events, sidebar
  // list, click-to-edit lookup) so an in-flight drag/resize is visually
  // committed before the Firestore round-trip completes.
  const effectiveMarks = useMemo(() => {
    if (pendingPatches.size === 0) return marks || [];
    return (marks || []).map((m) => {
      const patch = pendingPatches.get(m.id);
      return patch ? { ...m, ...patch } : m;
    });
  }, [marks, pendingPatches]);

  // Once the Firestore snapshot catches up with a pending patch (i.e. the
  // persisted mark's overlay-keys all match the patch values), drop the
  // patch — it's redundant and would otherwise pin stale-once-edited fields
  // through future snapshots. Comparison is shallow on each patch's keys.
  useEffect(() => {
    if (pendingPatches.size === 0) return;
    setPendingPatches((prev) => {
      let changed = false;
      const next = new Map(prev);
      for (const m of marks || []) {
        const patch = next.get(m.id);
        if (!patch) continue;
        const allMatch = Object.keys(patch).every((k) => m[k] === patch[k]);
        if (allMatch) {
          next.delete(m.id);
          changed = true;
        }
      }
      return changed ? next : prev;
    });
    // pendingPatches deliberately omitted: we read from the latest closure
    // via setState callback, and including it would loop on every clear.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [marks]);

  const markEvents = useMemo(() => {
    // Splice the optimistic placeholder (if any) into the marks feed so it
    // renders behind the open dialog. The synthetic id is stripped from any
    // resource lookup; click-to-edit ignores marks without a real id.
    const sourceMarks = pendingNewMark
      ? [...effectiveMarks, { ...pendingNewMark, id: "__pending-new-mark__" }]
      : effectiveMarks;
    return expandMarksToCalendarEvents(sourceMarks, visibleWindow.start, visibleWindow.end);
  }, [effectiveMarks, pendingNewMark, visibleWindow]);

  // Hidden-event display logic, per user feedback:
  //   - "Show hidden events" OFF → hidden events suppressed everywhere.
  //   - Week/day views (toggle ON) → hidden events render dimmed in their
  //     real time slot per design §5.5.
  //   - Month view (toggle ON) → hidden events do NOT render in cells (they
  //     would otherwise occupy precious cell slots). Instead each day with
  //     hidden events gets a "# hidden" pip in the cell's top-left, with a
  //     popover listing them. (See MonthDateHeader.)
  //
  // Fighting RBC's internal sort to push hidden events to "+N more" was
  // unreliable — the date-header pip approach gives the user a deterministic
  // affordance instead.
  // The rendered surface is "month-like" (hidden events become a per-day pip
  // instead of a row) when the RBC month view is active OR the mobile agenda is
  // showing. Deriving this from the rendered SURFACE — not RBC `view` alone —
  // stops a stale week/day `view` (left over after a Calendar→Agenda toggle)
  // from leaking hidden events into the mobile agenda as full rows (audit A-6d).
  const isAgendaSurface = isMobile && mobileCalView === "agenda";
  const monthLikeSurface = view === "month" || isAgendaSurface;
  const events = useMemo(
    () =>
      selectVisibleEvents({
        allEvents,
        markEvents,
        showHidden,
        monthLike: monthLikeSurface,
      }),
    [allEvents, markEvents, showHidden, monthLikeSurface],
  );

  // Day-bucketing basis for the month-view pips. Browser-local — matching the
  // react-big-calendar grid (which renders cells in the browser zone, no tz
  // prop) and the mobile agenda. This is the single basis PR-C unifies the
  // conflict pip, the hidden pip, and MonthDateHeader's cell-key lookup onto,
  // so a near-midnight event can never land on different cells per surface.
  const browserTz = getBrowserTimeZone();

  // The visible RBC month grid: a fixed 6-week (42-cell) block starting from the
  // week containing the 1st (Sunday, matching the enUS localizer). Bucketing the
  // pips to exactly this range covers every visible cell — including the
  // adjacent-month edge cells — while bounding the fan-out so a long all-day
  // hold can't expand into hundreds of buckets per render.
  const gridWindow = useMemo(() => {
    const monthStart = new Date(date.getFullYear(), date.getMonth(), 1);
    const start = startOfWeek(monthStart);
    const end = new Date(start);
    end.setDate(end.getDate() + 42);
    return { start, end };
  }, [date]);

  // Bucket hidden events by their LOCAL day, fanned across every day a multi-day
  // hidden event spans (via the shared bucketer), so MonthDateHeader can show a
  // "# hidden" pip + popover on each affected cell. Local keys match
  // localDayKey in the header (PR-C unification).
  const hiddenByDay = useMemo(() => {
    if (!showHidden) return new Map();
    const hidden = allEvents.filter((e) => e.resource?.hidden && e.start && e.end);
    const byDay = bucketEventsByLocalDay({
      events: hidden,
      timeZone: browserTz,
      windowStart: gridWindow.start,
      windowEnd: gridWindow.end,
    });
    const map = new Map();
    for (const [key, group] of byDay) {
      map.set(
        key,
        group.rows.map((r) => r.event)
      );
    }
    return map;
  }, [allEvents, showHidden, browserTz, gridWindow.start, gridWindow.end]);

  const externalStyleGetter = useMemo(() => buildEventStyleGetter(new Date()), []);
  const markStyleGetter = useMemo(() => buildMarkStyleGetter(), []);
  const eventPropGetter = useCallback(
    (event) => {
      const r = event?.resource;
      const base = r?.kind === "mark"
        ? markStyleGetter(event)
        : externalStyleGetter(event);

      // Phase 3b T0.5: add visual-distinction classes to the base result.
      // user-authored marks get a left-edge accent bar + corner icon (via CSS +
      // the calendarComponents.event renderer). Synced busy external events
      // get diagonal stripes so they read as "data from your calendar".
      if (r?.kind === "mark") {
        const levelClass = r.level === "PREFERRED"
          ? "availability-mark--preferred"
          : "availability-mark--feasible";
        base.className = [
          base.className || "",
          "availability-mark-user-authored",
          levelClass,
        ].filter(Boolean).join(" ");
      } else if (r?.kind === "external" && r?.calendarType !== "free") {
        base.className = [
          base.className || "",
          "availability-overlay-synced-busy",
        ].filter(Boolean).join(" ");
      }

      // Deep-link highlight (D16). `highlightActive` flips off after 8s so
      // the highlight fades — RBC re-renders without the styling.
      if (!highlightActive) return base;
      const isHighlighted =
        (highlightEventId &&
          r?.kind === "external" &&
          (r.eventId === highlightEventId || r.id === highlightEventId)) ||
        (highlightMarkId &&
          r?.kind === "mark" &&
          r.markId === highlightMarkId);
      if (!isHighlighted) return base;
      // Inline style trumps RBC's row overflow:hidden which clips Tailwind
      // ring-* box-shadows. Outline + box-shadow + scale + zIndex make the
      // event pop unmistakably. The `availability-deeplink-pulse` class
      // adds a keyframe pulse defined in src/index.css.
      return {
        ...base,
        className: [base?.className || "", "availability-deeplink-pulse"]
          .filter(Boolean)
          .join(" "),
        style: {
          ...(base?.style || {}),
          outline: "3px solid #f59e0b",
          outlineOffset: "2px",
          boxShadow: "0 0 0 8px rgba(245, 158, 11, 0.35), 0 4px 14px rgba(245, 158, 11, 0.5)",
          zIndex: 50,
          position: "relative",
        },
      };
    },
    [
      externalStyleGetter,
      markStyleGetter,
      highlightActive,
      highlightEventId,
      highlightMarkId,
    ]
  );

  // Scope the conflict-day fan-out to the visible RBC grid (gridWindow) so every
  // visible cell — including adjacent-month edge cells — can show its pip, while
  // still bounding the fan-out for long all-day holds.
  const conflictDays = useMemo(
    () =>
      detectConflictDays(events, {
        timeZone: browserTz,
        windowStart: gridWindow.start,
        windowEnd: gridWindow.end,
      }),
    [events, browserTz, gridWindow.start, gridWindow.end]
  );
  const calendarComponents = useMemo(
    () => ({
      event: MarkEventBlock,
      month: {
        dateHeader: makeMonthDateHeader({
          conflictDays,
          allEvents: events,
          hiddenByDay,
          onSelectHidden: handleSelectHidden,
          timeZone: browserTz,
        }),
      },
    }),
    // handleSelectHidden closes over externalEvents — re-create only when
    // the data sources change. (This component is small enough that we
    // accept a re-render per externalEvents update.)
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [conflictDays, events, hiddenByDay, externalEvents, browserTz]
  );

  // Default the week/day view scroll to 8 AM so users land on a useful
  // part of the hourly grid instead of midnight.
  const scrollToTime = useMemo(() => {
    const d = new Date();
    d.setHours(8, 0, 0, 0);
    return d;
  }, []);

  // Visually grey out past days (month view) and past hour slots (week/day)
  // so users focus on the future. Mirrors the dashboard calendar pattern.
  const dayPropGetter = (date) =>
    isBefore(date, startOfDay(new Date())) ? { className: "rbc-past-day" } : {};
  const slotPropGetter = (date) =>
    isBefore(date, startOfHour(new Date())) ? { className: "rbc-past-slot" } : {};

  function handleSelectEvent(rbcEvent) {
    const r = rbcEvent?.resource;
    if (!r) return;
    if (r.kind === "finalizedSession" && r.schedulerId) {
      window.location.href = `/scheduler/${r.schedulerId}`;
      return;
    }
    if (r.kind !== "external") return;
    // Find the source external event by composite (calendarId, eventId).
    const src = (externalEvents || []).find(
      (e) => e.calendarId === r.calendarId && e.eventId === r.eventId
    );
    if (!src) return;
    setHideTarget(src);
  }

  // Hidden-pip popover passes the RBC event of any hidden item the user
  // clicks; route external events into HideEventDialog (so they can be
  // unhidden) and finalized-session items into the scheduler page.
  function handleSelectHidden(rbcEvent) {
    handleSelectEvent(rbcEvent);
  }

  // Drag-resize handler with day-bound clamping for single-day marks and a
  // 15-min minimum-duration guard. Multi-day marks bypass the day clamp
  // (resizing across midnight is intentional for them). Writes are queued
  // through the debouncer so a burst of resize events collapses into one
  // Firestore update.
  const handleMarkResize = useCallback(
    ({ event, start, end }) => {
      if (event?.resource?.kind !== "mark") return;
      let s = start;
      let e = end;
      if (e.getTime() - s.getTime() < FIFTEEN_MIN_MS) return;
      const src = effectiveMarks.find((m) => m.id === event.resource.markId);
      if (src && isSingleDayMark(src)) {
        // DST-safe: clamp the resize to the mark's own local day [dayStart,
        // nextDayStart) — next midnight isn't always +24h on a transition day.
        const { dayStart, dayEnd: nextDayStart } = localDayBounds(
          new Date(src.startUtc),
          src.tzid || "UTC"
        );
        if (s < dayStart) s = dayStart;
        if (e > nextDayStart) e = nextDayStart;
        if (e.getTime() - s.getTime() < FIFTEEN_MIN_MS) return;
      }
      const patch = { startUtc: s.toISOString(), endUtc: e.toISOString() };
      setPendingPatches((prev) => {
        const next = new Map(prev);
        const merged = { ...(next.get(event.resource.markId) || {}), ...patch };
        next.set(event.resource.markId, merged);
        return next;
      });
      queueMarkUpdate(event.resource.markId, patch);
    },
    [effectiveMarks, queueMarkUpdate]
  );

  // Sort upcoming finalized sessions for the sidebar panel. Use the
  // group-color-enriched list so the chip render gets a resolved color
  // without having to call getGroupColor again per row.
  const upcomingSessions = useMemo(() => {
    const now = Date.now();
    return [...sessionsWithGroupColor]
      .filter((s) => Date.parse(s.endUtc) > now)
      .sort((a, b) => Date.parse(a.startUtc) - Date.parse(b.startUtc))
      .slice(0, 5);
  }, [sessionsWithGroupColor]);

  // Open the MarkEditorDialog in create-mode with sensible defaults derived
  // from the user's settings (defaultMarkStartHour / defaultMarkDurationHours)
  // anchored to today. Used by the no-linked-calendars empty-state CTA, so a
  // brand-new user can author a free-time mark without first having to
  // discover the click/drag affordance.
  // Shared seed for a brand-new mark: a non-all-day PREFERRED mark on `baseDate`,
  // using the user's default start hour + duration (falling back to the next
  // 15-min slot from now). Used by the FAB / empty-state (baseDate = today) and
  // by the mobile agenda's per-day "+" (baseDate = the tapped day) — audit A-6a.
  const buildCreateMarkInitial = useCallback(
    (baseDate) => {
      const base = baseDate instanceof Date ? baseDate : new Date(baseDate);
      const tzid = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const startHHMM = defaultMarkStartHour || pickStartHHMMFromNow(new Date());
      const endHHMM = pickEndHHMMFromStart(startHHMM, defaultMarkDurationHours);
      const startIso = composeUtcIso(base, startHHMM, tzid);
      // 24:00 end = local midnight of the NEXT calendar day. addDaysLocal is now
      // the shared DST-safe (calendar-arithmetic) helper, so this is correct even
      // on a 25-hour fall-back day (B-5/B-6 unification fixed the old +24h trap).
      const endIso =
        endHHMM === "24:00"
          ? composeUtcIso(addDaysLocal(base, 1), "00:00", tzid)
          : composeUtcIso(base, endHHMM, tzid);
      return {
        startUtc: startIso,
        endUtc: endIso,
        allDay: false,
        level: "PREFERRED",
        rrule: null,
        rruleUntil: null,
        tzid,
        exceptions: [],
      };
    },
    [defaultMarkStartHour, defaultMarkDurationHours]
  );
  const openCreateMarkDialog = useCallback(() => {
    setEditorState({ mode: "create", initial: buildCreateMarkInitial(new Date()) });
  }, [buildCreateMarkInitial]);
  // Per-day create from the mobile agenda's day-header "+": seed on the tapped day.
  const openCreateMarkDialogForDay = useCallback(
    (dayStart) => {
      setEditorState({ mode: "create", initial: buildCreateMarkInitial(dayStart) });
    },
    [buildCreateMarkInitial]
  );

  const noLinkedCalendars = linkedCalendars.length === 0;

  return (
    <div
      className={
        noLinkedCalendars
          ? "space-y-6"
          : "grid grid-cols-1 gap-6 lg:grid-cols-[280px_minmax(0,1fr)]"
      }
    >
      {noLinkedCalendars && (
        <EmptyAvailabilityState
          onLinkClick={onLinkClick}
          onAddMarkClick={openCreateMarkDialog}
        />
      )}
      {!noLinkedCalendars && (
      <aside className="min-w-0 space-y-4 rounded-2xl border border-slate-200 bg-white/70 p-4 text-sm dark:border-slate-700 dark:bg-slate-900/60">
        <h3 className="font-semibold">Linked calendars</h3>
        <ul className="space-y-1">
          {linkedCalendars.map((cal) => (
            <li key={cal.id}>
              <button
                type="button"
                onClick={() => setEditTarget(cal)}
                className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left transition-colors hover:bg-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 dark:hover:bg-slate-800"
                aria-label={`Edit ${cal.googleCalendarSummary || cal.id}`}
              >
                <span
                  aria-hidden="true"
                  className="inline-block h-3 w-3 shrink-0 rounded-full"
                  style={{ backgroundColor: cal.color || "#94a3b8" }}
                />
                <span className="truncate">
                  {cal.googleCalendarSummary || cal.id}
                </span>
                <span className="ml-auto flex items-center gap-1">
                  <span className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">
                    {cal.calendarType}
                  </span>
                  {cal.calendarType === "free" && cal.defaultVoteLevel && (
                    <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-medium text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300">
                      {cal.defaultVoteLevel === "PREFERRED" ? "Preferred" : "Feasible"}
                    </span>
                  )}
                  {(() => {
                    const n = eventCountByCalendarId.get(cal.id) || 0;
                    return (
                      <span
                        className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-medium tabular-nums text-slate-600 dark:bg-slate-800 dark:text-slate-300"
                        aria-label={`${n} cached events`}
                        title={`${n} cached events`}
                      >
                        {n}
                      </span>
                    );
                  })()}
                </span>
              </button>
            </li>
          ))}
        </ul>
        <div>
          <div className="flex items-center justify-between gap-2">
            <h3 className="font-semibold">My marks</h3>
            <button
              type="button"
              onClick={openCreateMarkDialog}
              data-testid="availability-add-mark-button"
              className="inline-flex min-h-[40px] items-center gap-1 rounded-lg bg-brand-primary px-2.5 py-1.5 text-xs font-semibold text-white shadow-sm transition-colors hover:bg-brand-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            >
              <Plus className="h-3.5 w-3.5" /> Add mark
            </button>
          </div>
          {effectiveMarks.length === 0 ? (
            <p
              data-testid="my-marks-empty"
              className="mt-2 text-xs text-slate-500 dark:text-slate-400"
            >
              Tap “Add mark” to set your free or preferred times — or drag on the calendar.
            </p>
          ) : (
            <ul data-testid="my-marks-list" className="mt-2 space-y-1">
              {effectiveMarks.map((m) => (
                <li key={m.id}>
                  <button
                    type="button"
                    onClick={() =>
                      setEditorState({ mode: "edit", initial: { ...m } })
                    }
                    className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left transition-colors hover:bg-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 dark:hover:bg-slate-800"
                  >
                    <span
                      aria-hidden="true"
                      className="inline-block h-3 w-3 shrink-0 rounded-full"
                      style={{
                        backgroundColor:
                          m.level === "PREFERRED" ? "#15803d" : "#1d4ed8",
                      }}
                    />
                    <span className="flex-1 truncate text-sm">
                      {(typeof m.title === "string" && m.title.trim()) ||
                        (m.level === "PREFERRED" ? "Preferred" : "Feasible")}
                      {m.rrule ? " · recurring" : ""}
                    </span>
                    <span className="text-xs text-slate-500 dark:text-slate-400">
                      {new Date(m.startUtc).toLocaleDateString(undefined, {
                        month: "short",
                        day: "numeric",
                      })}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div>
          <h3 className="font-semibold">Upcoming sessions</h3>
          {upcomingSessions.length === 0 ? (
            <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
              No finalized sessions in the future.
            </p>
          ) : (
            <ul className="mt-2 space-y-1">
              {upcomingSessions.map((s) => {
                return (
                  <li
                    key={s.schedulerId}
                    className="group flex items-start gap-1 rounded-lg transition-colors hover:bg-slate-100 dark:hover:bg-slate-800"
                  >
                    <RouterLink
                      to={`/scheduler/${s.schedulerId}`}
                      className="flex min-w-0 flex-1 flex-col gap-1 px-2 py-1.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded-lg"
                    >
                      <span className="block truncate text-sm">
                        {s.title || "Finalized session"}
                      </span>
                      <span className="block text-xs text-slate-500 dark:text-slate-400">
                        {new Date(s.startUtc).toLocaleString(undefined, {
                          month: "short",
                          day: "numeric",
                          hour: "numeric",
                          minute: "2-digit",
                        })}
                        {s.hidden && (
                          <span className="ml-1 text-rose-500 dark:text-rose-300">
                            · RSVP&apos;d no
                          </span>
                        )}
                      </span>
                      {s.questingGroupName && (
                        <span
                          className="inline-flex h-5 w-fit items-center gap-1 rounded-full px-2 text-[10px] font-semibold leading-none text-white"
                          style={{ backgroundColor: s.questingGroupColor || "hsl(210 70% 42%)" }}
                        >
                          <Users className="h-3 w-3" aria-hidden="true" />
                          <span className="truncate max-w-[10rem]">
                            {s.questingGroupName}
                          </span>
                        </span>
                      )}
                    </RouterLink>
                    <SessionCalendarControl
                      scheduler={s}
                      viewerUid={uid}
                      slot={{ start: s.startUtc, end: s.endUtc }}
                      viewer={calendarViewer}
                      filenameBase={s.schedulerId}
                      variant="list-icon"
                    />
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </aside>
      )}
      {isMobile && (
        <div
          role="group"
          aria-label="Availability view"
          className="flex w-fit gap-2 rounded-full border border-slate-200 bg-white px-2 py-1 dark:border-slate-600 dark:bg-slate-700"
        >
          {[
            { key: "agenda", label: "Agenda" },
            { key: "calendar", label: "Calendar" },
          ].map((opt) => (
            <button
              key={opt.key}
              type="button"
              onClick={() => setMobileCalView(opt.key)}
              aria-pressed={mobileCalView === opt.key}
              className={`inline-flex min-h-[36px] items-center justify-center rounded-full px-3 text-xs font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${
                mobileCalView === opt.key
                  ? "bg-brand-primary text-white"
                  : "text-slate-500 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-600"
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      )}
      {isMobile && mobileCalView === "agenda" ? (
        <section
          aria-label="Availability agenda"
          className="min-w-0 rounded-2xl border border-slate-200 bg-white p-3 pb-24 dark:border-slate-700 dark:bg-slate-900"
        >
          <MobileAvailabilityAgenda
            events={events}
            date={date}
            hiddenByDay={hiddenByDay}
            onNavigate={setDate}
            onEditMark={(id) => {
              const src = effectiveMarks.find((m) => m.id === id);
              if (src) setEditorState({ mode: "edit", initial: { ...src } });
            }}
            onSelectEvent={handleSelectEvent}
            onCreateMark={openCreateMarkDialog}
            onCreateMarkForDay={openCreateMarkDialogForDay}
          />
        </section>
      ) : (
      <section
        aria-label="Calendar view"
        className={`min-w-0 rounded-2xl border border-slate-200 bg-white p-2 dark:border-slate-700 dark:bg-slate-900 ${isMobile ? "pb-24" : ""}`}
      >
        {/* Below md, give the RBC grid a readable min-width and let the region
            scroll horizontally (B2c, mirrors the scheduler) so month/week/day are
            navigable on a phone. md+ is a no-op (full-width, no scroll). */}
        <div
          data-testid="availability-calendar-scroll"
          className="-mx-1 overflow-x-auto px-1 md:mx-0 md:overflow-x-visible md:px-0"
        >
        {/* Day view is a single column — it fits a phone, so skip the 44rem
            min-width (no pointless horizontal scroll). Month/week keep it for
            their 7 columns. md+ = no-op. */}
        <div className={`${view === "day" ? "min-w-0" : "min-w-[44rem]"} md:min-w-0`}>
        <div className="availability-calendar" style={{ height: "640px" }}>
          <DragAndDropCalendar
            localizer={localizer}
            events={events}
            startAccessor="start"
            endAccessor="end"
            views={["month", "week", "day"]}
            view={view}
            onView={setView}
            date={date}
            onNavigate={setDate}
            selectable="ignoreEvents"
            step={15}
            timeslots={4}
            onSelectSlot={(slotInfo) => {
              const tzid = Intl.DateTimeFormat().resolvedOptions().timeZone;
              let startIso;
              let endIso;
              if (view === "month") {
                // Month view: clicked-day's date stays; pick a sensible time
                // window from the user's defaults (settings popover) so the
                // dialog opens with a non-all-day mark on that day.
                const startHHMM =
                  defaultMarkStartHour || pickStartHHMMFromNow(new Date());
                const endHHMM = pickEndHHMMFromStart(
                  startHHMM,
                  defaultMarkDurationHours
                );
                startIso = composeUtcIso(slotInfo.start, startHHMM, tzid);
                endIso =
                  endHHMM === "24:00"
                    ? composeUtcIso(addDaysLocal(slotInfo.start, 1), "00:00", tzid)
                    : composeUtcIso(slotInfo.start, endHHMM, tzid);
              } else {
                // Week/day: start is the clicked time (RBC's step={15} keeps
                // it 15-min aligned). For a single click, RBC reports a
                // 15-min span — too short for a useful default — so we
                // override the end with start + defaultMarkDurationHours.
                // For an explicit drag-select (multi-slot span), respect
                // the user's chosen range.
                const start = slotInfo.start;
                const isClick =
                  slotInfo.action === "click" ||
                  slotInfo.action === "doubleClick" ||
                  (slotInfo.end?.getTime?.() - start?.getTime?.()) <= 15 * 60 * 1000;
                startIso = start.toISOString();
                if (isClick) {
                  const endMs = start.getTime() + Math.round((defaultMarkDurationHours || 1) * 3600 * 1000);
                  // Clamp to end-of-day so we don't accidentally span midnight
                  // for a single click (DST-safe next local midnight).
                  const dayEnd = localDayBounds(start, tzid).dayEnd.getTime();
                  endIso = new Date(Math.min(endMs, dayEnd)).toISOString();
                } else {
                  endIso = slotInfo.end.toISOString();
                }
              }
              const initial = {
                startUtc: startIso,
                endUtc: endIso,
                allDay: false,
                level: "PREFERRED",
                rrule: null,
                rruleUntil: null,
                tzid,
                exceptions: [],
              };
              setPendingNewMark(initial);
              setEditorState({ mode: "create", initial });
            }}
            onSelectEvent={(rbcEvent) => {
              if (rbcEvent?.resource?.kind === "mark") {
                const src = effectiveMarks.find(
                  (m) => m.id === rbcEvent.resource.markId
                );
                if (!src) return;
                setEditorState({ mode: "edit", initial: { ...src } });
                return;
              }
              handleSelectEvent(rbcEvent);
            }}
            onEventDrop={({ event, start, end }) => {
              if (event?.resource?.kind !== "mark") return;
              const patch = { startUtc: start.toISOString(), endUtc: end.toISOString() };
              setPendingPatches((prev) => {
                const next = new Map(prev);
                const merged = { ...(next.get(event.resource.markId) || {}), ...patch };
                next.set(event.resource.markId, merged);
                return next;
              });
              queueMarkUpdate(event.resource.markId, patch);
            }}
            onEventResize={handleMarkResize}
            draggableAccessor={(event) =>
              event?.resource?.kind === "mark" &&
              event?.resource?.segmentIndex == null
            }
            resizableAccessor={(event) =>
              event?.resource?.kind === "mark" &&
              event?.resource?.segmentIndex == null
            }
            eventPropGetter={eventPropGetter}
            dayPropGetter={dayPropGetter}
            slotPropGetter={slotPropGetter}
            scrollToTime={scrollToTime}
            components={calendarComponents}
            popup
          />
        </div>
        </div>
        </div>
      </section>
      )}
      {isMobile && !noLinkedCalendars && (
        <button
          type="button"
          onClick={openCreateMarkDialog}
          data-testid="availability-add-mark-fab"
          aria-label="Add availability mark"
          style={{ bottom: "calc(env(safe-area-inset-bottom, 0px) + 1.5rem)" }}
          className="fixed right-6 z-40 inline-flex h-14 w-14 items-center justify-center rounded-full bg-brand-primary text-white shadow-lg transition-colors hover:bg-brand-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        >
          <Plus className="h-6 w-6" aria-hidden="true" />
        </button>
      )}
      <HideEventDialog
        open={!!hideTarget}
        onOpenChange={(open) => {
          if (!open) setHideTarget(null);
        }}
        uid={uid}
        event={hideTarget}
        hideOverrides={hideOverrides}
      />
      <EditLinkedCalendarDialog
        open={!!editTarget}
        onOpenChange={(open) => {
          if (!open) setEditTarget(null);
        }}
        calendar={editTarget}
      />
      <MarkEditorDialog
        open={!!editorState}
        onOpenChange={(open) => {
          if (!open) {
            setEditorState(null);
            // Clear the optimistic placeholder. If the user clicked Save,
            // the persisted mark from Firestore replaces it via the
            // snapshot listener (local-cache write fires nearly instantly,
            // imperceptible gap). If they Cancelled, the placeholder just
            // vanishes.
            setPendingNewMark(null);
          }
        }}
        uid={uid}
        mode={editorState?.mode || "create"}
        initial={editorState?.initial || null}
        onSaved={() => {
          setEditorState(null);
          setPendingNewMark(null);
        }}
      />
    </div>
  );
}
